import React, { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCcw, PhoneCall, Users } from "lucide-react";
import { getChatbotLeadsAPI } from "../services/apicollection";

function formatDateTime(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function usePaginated(rows, pageSize = 10) {
  const [page, setPage] = useState(1);
  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const endIndexExclusive = Math.min(startIndex + pageSize, total);
  const pageRows = rows.slice(startIndex, endIndexExclusive);
  return { page: safePage, setPage, total, totalPages, startIndex, endIndexExclusive, pageRows };
}

function Pager({ p }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-400">
      <div>
        Showing{" "}
        <span className="font-semibold text-slate-200">
          {p.startIndex + 1}–{p.endIndexExclusive}
        </span>{" "}
        of <span className="font-semibold text-slate-200">{p.total}</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => p.setPage((x) => Math.max(1, x - 1))}
          disabled={p.page === 1}
          className="rounded-md bg-[#0f1c2e] px-2 py-1 ring-1 ring-slate-200 hover:bg-[#0d1b2e] disabled:opacity-60"
        >
          Prev
        </button>
        <span className="tabular-nums">
          Page <span className="font-semibold">{p.page}</span> /{" "}
          <span className="font-semibold">{p.totalPages}</span>
        </span>
        <button
          type="button"
          onClick={() => p.setPage((x) => Math.min(p.totalPages, x + 1))}
          disabled={p.page === p.totalPages}
          className="rounded-md bg-[#0f1c2e] px-2 py-1 ring-1 ring-slate-200 hover:bg-[#0d1b2e] disabled:opacity-60"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function LeadsTable({ leads }) {
  const p = usePaginated(leads);

  if (leads.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12 text-center">
        <Users size={36} className="text-slate-300" />
        <p className="mt-2 text-sm text-slate-500">No leads captured yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Pager p={p} />
      <div className="relative overflow-hidden rounded-2xl bg-[#0f1c2e] shadow-[0_20px_50px_rgba(0,0,0,0.08)]">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left">
            <thead className="border-b border-[#1d3350] bg-[#0d1b2e]">
              <tr>
                <th className="px-6 py-4 text-xs font-bold tracking-wide text-slate-500 uppercase">Name</th>
                <th className="px-6 py-4 text-xs font-bold tracking-wide text-slate-500 uppercase">Phone</th>
                <th className="px-6 py-4 text-xs font-bold tracking-wide text-slate-500 uppercase">Asked about</th>
                <th className="px-6 py-4 text-xs font-bold tracking-wide text-slate-500 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {p.pageRows.map((l) => (
                <tr key={l?._id} className="transition-colors hover:bg-[#0d1b2e]">
                  <td className="px-6 py-4 text-sm text-slate-200 whitespace-nowrap capitalize">
                    {l?.name || "—"}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap">
                    <a
                      href={`tel:${l?.phone}`}
                      className="text-blue-400 hover:underline inline-flex items-center gap-1"
                    >
                      <PhoneCall size={14} />
                      {l?.phone || "—"}
                    </a>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-300 max-w-[280px]">
                    <div className="line-clamp-2">{l?.lastQuestion || "—"}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap">
                    {formatDateTime(l?.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const ChatbotQueriesPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError(null);
    const controller = new AbortController();
    try {
      const lRes = await getChatbotLeadsAPI({ signal: controller.signal });

      if (lRes?.status && Array.isArray(lRes.data)) {
        setLeads(lRes.data);
      } else {
        setLeads([]);
        setError(lRes?.message || "Failed to load chatbot leads");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load chatbot leads");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-200">Chatbot Leads</h2>
        <button
          type="button"
          onClick={() => fetchLeads()}
          className="inline-flex items-center gap-2 rounded-lg bg-[#0f1c2e] px-3 py-2 text-sm font-medium text-slate-200 ring-1 ring-slate-200 transition-colors hover:bg-[#0d1b2e]"
          title="Refresh"
        >
          <RefreshCcw size={16} />
          Refresh
        </button>
      </div>

      {!loading && !error && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-lg border border-[#1d3350] bg-[#0f1c2e] px-4 py-3">
            <p className="text-xs text-slate-400 flex items-center gap-1">
              <Users size={14} className="text-blue-400" /> Leads captured
            </p>
            <p className="text-xl font-semibold text-blue-400">{leads.length}</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12">
          <Loader2 size={28} className="animate-spin text-slate-400" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-4 text-center text-sm text-red-700">
          {error}
        </div>
      ) : (
        <LeadsTable leads={leads} />
      )}
    </div>
  );
};

export default ChatbotQueriesPage;